import React, { useState, useEffect } from 'react';
import { Plus, Eye, Settings, X } from 'lucide-react';
import { Company, ExchangeRate } from '../../../services/api';
import { ApiService } from '../../../services/api';
import { useWallet } from '@solana/wallet-adapter-react';
import { SignedTransactionService } from '../../../services/signedTransactionService';
import toast from 'react-hot-toast';

interface AllowedCompaniesModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedCompany: Company | null;
  companies: Company[];
  onRefresh: () => void;
}

interface ExchangeRateForm {
  receiverCompanyId: string;
  rate: string;
}

interface EditExchangeRateForm {
  rate: string;
}

const AllowedCompaniesModal: React.FC<AllowedCompaniesModalProps> = ({
  isOpen,
  onClose,
  selectedCompany,
  companies,
  onRefresh
}) => {
  const [allowedCompanies, setAllowedCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddExchangeRateModal, setShowAddExchangeRateModal] = useState(false);
  const [showViewExchangeRateModal, setShowViewExchangeRateModal] = useState(false);
  const [showEditExchangeRateModal, setShowEditExchangeRateModal] = useState(false);
  const [selectedExchangeRate, setSelectedExchangeRate] = useState<ExchangeRate | null>(null);
  const [exchangeRateForm, setExchangeRateForm] = useState<ExchangeRateForm>({
    receiverCompanyId: '',
    rate: '',
  });
  const [editExchangeRateForm, setEditExchangeRateForm] = useState<EditExchangeRateForm>({
    rate: '',
  });

  const { connected, publicKey, signTransaction } = useWallet();

  useEffect(() => {
    if (isOpen && selectedCompany) {
      fetchAllowedCompanies();
    }
  }, [isOpen, selectedCompany]);

  const fetchAllowedCompanies = async () => {
    if (!selectedCompany) return;
    
    setLoading(true);
    try {
      const allowed = await ApiService.getAllowedCompaniesForSender(selectedCompany.companyWallet);
      setAllowedCompanies(allowed.map(a => a.company));
    } catch (error: any) {
      console.error('Failed to load allowed companies:', error);
      toast.error('Failed to load allowed companies');
    } finally {
      setLoading(false);
    }
  };

  const handleAddExchangeRate = () => {
    setExchangeRateForm({
      receiverCompanyId: '',
      rate: '',
    });
    setShowAddExchangeRateModal(true);
  };

  const handleAddExchangeRateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCompany || !exchangeRateForm.receiverCompanyId || !exchangeRateForm.rate) return;

    // Check if wallet is connected
    if (!connected || !publicKey || !signTransaction) {
      toast.error('Please connect your wallet to set exchange rate');
      return;
    }

    try {
      const wallet = {
        publicKey,
        signTransaction,
      };

      const receiverCompany = companies.find(c => c.id === exchangeRateForm.receiverCompanyId);
      if (!receiverCompany) {
        toast.error('Receiver company not found');
        return;
      }

      // Build the transaction
      const transactionData = await ApiService.buildSetExchangeRateTransaction(
        selectedCompany.companyWallet,
        receiverCompany.companyWallet,
        parseFloat(exchangeRateForm.rate) * 100, // Convert to basis points
        publicKey.toString()
      );

      // Sign the transaction
      const signedTransaction = await SignedTransactionService.signAndSendTransaction(
        transactionData,
        wallet
      );

      if (signedTransaction.success && signedTransaction.signature) {
        // Process the result
        await ApiService.processSetExchangeRateResult(
          selectedCompany.companyWallet,
          receiverCompany.companyWallet,
          parseFloat(exchangeRateForm.rate) * 100,
          signedTransaction.signature,
          true
        );

        toast.success('Exchange rate set successfully');
        setShowAddExchangeRateModal(false);
        setExchangeRateForm({ receiverCompanyId: '', rate: '' });
        await fetchAllowedCompanies();
        onRefresh();
      } else {
        toast.error('Failed to sign transaction');
      }
    } catch (error: any) {
      console.error('Failed to set exchange rate:', error);
      toast.error(error.response?.data?.message || 'Failed to set exchange rate');
    }
  };

  const handleViewExchangeRate = async (receiverCompany: Company) => {
    if (!selectedCompany) return;
    
    try {
      const exchangeRate = await ApiService.getExchangeRate(
        selectedCompany.companyWallet,
        receiverCompany.companyWallet
      );
      
      if (exchangeRate) {
        setSelectedExchangeRate(exchangeRate);
        setShowViewExchangeRateModal(true);
      } else {
        toast.error('Exchange rate not found');
      }
    } catch (error: any) {
      console.error('Failed to get exchange rate:', error);
      toast.error('Failed to get exchange rate');
    }
  };

  const handleEditExchangeRate = async (receiverCompany: Company) => {
    if (!selectedCompany) return;
    
    try {
      const exchangeRate = await ApiService.getExchangeRate(
        selectedCompany.companyWallet,
        receiverCompany.companyWallet
      );
      
      if (exchangeRate) {
        setSelectedExchangeRate(exchangeRate);
        setEditExchangeRateForm({
          rate: (exchangeRate.rate / 100).toString(), // Convert from basis points to percentage
        });
        setShowEditExchangeRateModal(true);
      } else {
        toast.error('Exchange rate not found');
      }
    } catch (error: any) {
      console.error('Failed to get exchange rate:', error);
      toast.error('Failed to get exchange rate');
    }
  };

  const handleEditExchangeRateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCompany || !selectedExchangeRate || !editExchangeRateForm.rate) return;

    // Check if wallet is connected
    if (!connected || !publicKey || !signTransaction) {
      toast.error('Please connect your wallet to update exchange rate');
      return;
    }

    try {
      const wallet = {
        publicKey,
        signTransaction,
      };

      // Build the transaction
      const transactionData = await ApiService.buildSetExchangeRateTransaction(
        selectedCompany.companyWallet,
        selectedExchangeRate.receiverCompanyWallet,
        parseFloat(editExchangeRateForm.rate) * 100, // Convert to basis points
        publicKey.toString()
      );

      // Sign the transaction
      const signedTransaction = await SignedTransactionService.signAndSendTransaction(
        transactionData,
        wallet
      );

      if (signedTransaction.success && signedTransaction.signature) {
        // Process the result
        await ApiService.processSetExchangeRateResult(
          selectedCompany.companyWallet,
          selectedExchangeRate.receiverCompanyWallet,
          parseFloat(editExchangeRateForm.rate) * 100,
          signedTransaction.signature,
          true
        );

        toast.success('Exchange rate updated successfully');
        setShowEditExchangeRateModal(false);
        setEditExchangeRateForm({ rate: '' });
        setSelectedExchangeRate(null);
        await fetchAllowedCompanies();
        onRefresh();
      } else {
        toast.error('Failed to sign transaction');
      }
    } catch (error: any) {
      console.error('Failed to update exchange rate:', error);
      toast.error(error.response?.data?.message || 'Failed to update exchange rate');
    }
  };

  if (!isOpen || !selectedCompany) return null;

  return (
    <>
      {/* Main Modal */}
      <div className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm flex items-center justify-center overflow-y-auto h-full w-full z-50">
        <div className="relative top-10 mx-auto p-5 w-4/5 max-w-4xl shadow-lg rounded-md bg-[#182233]">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-white">
              Manage Allowed Companies - {selectedCompany.companyName}
            </h3>
            <button 
              onClick={onClose} 
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          
          <div className="mb-4">
            <p className="text-sm text-gray-400">
              Companies that {selectedCompany.companyName} can send money to:
            </p>
          </div>

          <div className="mb-4">
            <button
              onClick={handleAddExchangeRate}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#2777EF] hover:bg-[#0053E2]"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Allowed Company
            </button>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2777EF] mx-auto"></div>
              <p className="text-gray-400 mt-2">Loading allowed companies...</p>
            </div>
          ) : allowedCompanies.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-600">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-[#182233] divide-y divide-gray-600">
                  {allowedCompanies.map((company) => (
                    <tr key={company.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-white">{company.companyName}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                        {company.companyEmail}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          company.status === 'ACTIVE' ? 'bg-green-100 text-green-800' :
                          company.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {company.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleViewExchangeRate(company)}
                            className="text-blue-600 hover:text-blue-900"
                            title="View Exchange Rate"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleEditExchangeRate(company)}
                            className="text-green-600 hover:text-green-900"
                            title="Edit Exchange Rate"
                          >
                            <Settings className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-400">No allowed companies found. Add companies to enable transactions.</p>
            </div>
          )}

          <div className="flex justify-end pt-4">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-[#567095]/30 text-sm font-medium rounded-md text-white bg-[#182233] hover:bg-[#567095]/20"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Add Exchange Rate Modal */}
      {showAddExchangeRateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm flex items-center justify-center overflow-y-auto h-full w-full z-[100]">
          <div className="relative top-20 mx-auto p-5 w-96 shadow-lg rounded-md bg-[#182233]">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-white">Add Allowed Company</h3>
              <button 
                onClick={() => setShowAddExchangeRateModal(false)} 
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={handleAddExchangeRateSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Receiver Company
                </label>
                <select
                  value={exchangeRateForm.receiverCompanyId}
                  onChange={(e) => setExchangeRateForm({ ...exchangeRateForm, receiverCompanyId: e.target.value })}
                  className="w-full appearance-none rounded-md border-0 bg-[#182233] px-3 py-2 placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-[#2777EF] sm:text-sm"
                  required
                >
                  <option value="">Select a company...</option>
                  {companies
                    .filter(c => 
                      c.id !== selectedCompany.id && 
                      c.status === 'ACTIVE' &&
                      !allowedCompanies.some(allowed => allowed.id === c.id)
                    )
                    .map(company => (
                      <option key={company.id} value={company.id}>
                        {company.companyName}
                      </option>
                    ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Exchange Rate (%)
                </label>
                <input
                  type="number"
                  value={exchangeRateForm.rate}
                  onChange={(e) => setExchangeRateForm({ ...exchangeRateForm, rate: e.target.value })}
                  className="w-full appearance-none rounded-md border-0 bg-[#567095]/20 px-3 py-2 placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-[#2777EF] sm:text-sm"
                  placeholder="Enter exchange rate percentage"
                  min="0.01"
                  max="1000"
                  step="0.01"
                  required
                />
                <p className="text-xs text-gray-400 mt-1">Exchange rate as percentage (e.g., 1.5 for 1.5%)</p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAddExchangeRateModal(false)}
                  className="px-4 py-2 border border-[#567095]/30 text-sm font-medium rounded-md text-white bg-[#182233] hover:bg-[#567095]/20"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#2777EF] hover:bg-[#0053E2]"
                >
                  Add Company
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Exchange Rate Modal */}
      {showViewExchangeRateModal && selectedExchangeRate && (
        <div className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm flex items-center justify-center overflow-y-auto h-full w-full z-[100]">
          <div className="relative top-20 mx-auto p-5 w-96 shadow-lg rounded-md bg-[#182233]">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-white">Exchange Rate Details</h3>
              <button 
                onClick={() => setShowViewExchangeRateModal(false)} 
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Sender Company</label>
                <p className="text-white">{selectedExchangeRate.senderCompany?.companyName || 'Unknown'}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Receiver Company</label>
                <p className="text-white">{selectedExchangeRate.receiverCompany?.companyName || 'Unknown'}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Exchange Rate</label>
                <p className="text-white">{(selectedExchangeRate.rate / 100).toFixed(2)}%</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Status</label>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  selectedExchangeRate.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {selectedExchangeRate.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <button
                onClick={() => {
                  setShowViewExchangeRateModal(false);
                  handleEditExchangeRate(selectedExchangeRate.receiverCompany);
                }}
                className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
              >
                Edit Rate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Exchange Rate Modal */}
      {showEditExchangeRateModal && selectedExchangeRate && (
        <div className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm flex items-center justify-center overflow-y-auto h-full w-full z-[100]">
          <div className="relative top-20 mx-auto p-5 w-96 shadow-lg rounded-md bg-[#182233]">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-white">Edit Exchange Rate</h3>
              <button 
                onClick={() => setShowEditExchangeRateModal(false)} 
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={handleEditExchangeRateSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Exchange Rate (%)
                </label>
                <input
                  type="number"
                  value={editExchangeRateForm.rate}
                  onChange={(e) => setEditExchangeRateForm({ ...editExchangeRateForm, rate: e.target.value })}
                  className="w-full appearance-none rounded-md border-0 bg-[#567095]/20 px-3 py-2 placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-[#2777EF] sm:text-sm"
                  placeholder="Enter exchange rate percentage"
                  min="0.01"
                  max="1000"
                  step="0.01"
                  required
                />
                <p className="text-xs text-gray-400 mt-1">Exchange rate as percentage (e.g., 1.5 for 1.5%)</p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowEditExchangeRateModal(false)}
                  className="px-4 py-2 border border-[#567095]/30 text-sm font-medium rounded-md text-white bg-[#182233] hover:bg-[#567095]/20"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                >
                  Update Rate
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default AllowedCompaniesModal;
